import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { AlertCircle, AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Download, History, Layers, MessageSquare, Pencil, Plus, RotateCcw, Search, Trash2, X } from 'lucide-react';
import functionalCoverageService from './services/functionalCoverageService';

const REVIEW_LABELS = { to_review: 'À revoir', in_review: 'En revue', validated: 'Validée PO' };
const REVIEW_CLS = {
  to_review: 'bg-gray-100 text-gray-700',
  in_review: 'bg-amber-100 text-amber-800',
  validated: 'bg-green-100 text-green-800',
};
const CRIT_CLS = {
  P1: 'border-red-300 text-red-700 bg-red-50',
  P2: 'border-blue-300 text-blue-700 bg-blue-50',
  P3: 'border-gray-300 text-gray-600 bg-gray-50',
};
const ACTION_LABELS = {
  'feature.add': 'Fonctionnalité ajoutée',
  'feature.update': 'Fonctionnalité modifiée',
  'feature.delete': 'Fonctionnalité supprimée',
  'feature.restore': 'Fonctionnalité restaurée',
  'capability.add': 'Capacité ajoutée',
  'capability.update': 'Capacité modifiée',
  'capability.delete': 'Capacité supprimée',
  'capability.restore': 'Capacité restaurée',
  'comment.add': 'Commentaire',
};
const FIELD_LABELS = { criticality: 'criticité', since: 'depuis', reviewStatus: 'revue', name: 'nom', description: 'description', status: 'statut' };

// Une capacité hors périmètre sort du dénominateur : elle n'est ni testée ni manquante.
const inScope = (cap) => cap.status !== 'out_of_scope';
const isTested = (cap) => cap.tests > 0;
const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0);
const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;
const testsWarning = (n) => (n ? `\n\n${plural(n, 'test')} y ${n > 1 ? 'sont rattachés' : 'est rattaché'} : ils ne compteront plus dans la couverture.` : '');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

function stats(features) {
  const caps = features.flatMap((f) => f.capabilities.filter(inScope));
  const p1 = features.filter((f) => f.criticality === 'P1').flatMap((f) => f.capabilities.filter(inScope));
  return {
    features: features.length,
    untested: features.filter((f) => !f.capabilities.some((c) => inScope(c) && isTested(c))).length,
    caps: caps.length,
    capsTested: caps.filter(isTested).length,
    p1: p1.length,
    p1Tested: p1.filter(isTested).length,
    validated: features.filter((f) => f.reviewStatus === 'validated').length,
  };
}

function Bar({ value, total, color = 'bg-green-500' }) {
  return (
    <div className="flex items-center gap-2 min-w-[150px]">
      <div className="h-2 w-24 rounded bg-gray-200 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct(value, total)}%` }} />
      </div>
      <span className="text-sm tabular-nums text-gray-700 w-10 text-right">{pct(value, total)}%</span>
      <span className="text-xs tabular-nums text-gray-500">{value}/{total}</span>
    </div>
  );
}

function InlineText({ value, onSave, multiline = false, placeholder, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => setDraft(value || ''), [value]);
  if (!editing) {
    return (
      <span className={`group inline-flex items-start gap-1 ${className}`}>
        <span>{value || <span className="italic text-gray-400">{placeholder}</span>}</span>
        <button type="button" onClick={() => setEditing(true)} title="Modifier" className="opacity-40 group-hover:opacity-100 text-gray-500 hover:text-blue-600 mt-0.5">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }
  const save = async () => {
    if (draft.trim() === (value || '').trim()) return setEditing(false);
    if (await onSave(draft)) setEditing(false);
  };
  const Field = multiline ? 'textarea' : 'input';
  return (
    <span className="flex flex-1 items-start gap-2">
      <Field
        autoFocus
        value={draft}
        rows={multiline ? 3 : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          if (e.key === 'Enter' && !multiline) save();
        }}
        className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button type="button" onClick={save} className="px-2 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">OK</button>
      <button type="button" onClick={() => { setDraft(value || ''); setEditing(false); }} title="Annuler" className="p-1 text-gray-500 hover:text-gray-800">
        <X className="w-4 h-4" />
      </button>
    </span>
  );
}

function CapabilityRow({ cap, act }) {
  const outOfScope = cap.status === 'out_of_scope';
  const state = outOfScope ? 'out' : isTested(cap) ? 'yes' : 'no';
  const rowCls = { out: 'bg-gray-50 text-gray-400', yes: '', no: 'bg-orange-50' }[state];
  const pill = {
    out: <span className="text-xs font-medium text-gray-500">hors périmètre</span>,
    yes: <span className="text-xs font-medium text-green-700">testée</span>,
    no: <span className="text-xs font-medium text-orange-700">non testée</span>,
  }[state];
  const remove = () => {
    const hint = outOfScope ? '' : '\n\nSi elle existe mais n\'a pas à être testée, « Hors périmètre » est plus juste.';
    if (window.confirm(`Supprimer la capacité « ${cap.name} » ?${testsWarning(cap.tests)}${hint}\n\nRestaurable depuis l'onglet Supprimées.`)) {
      act(functionalCoverageService.deleteCapability(cap.id), 'Capacité supprimée');
    }
  };
  return (
    <li className={`grid grid-cols-[100px_minmax(0,1fr)_auto] gap-3 items-start px-3 py-2 rounded ${rowCls}`}>
      <span className="pt-0.5">{pill}</span>
      <span className={`text-sm ${outOfScope ? 'line-through' : 'text-gray-800'}`}>
        <InlineText value={cap.name} onSave={(name) => act(functionalCoverageService.updateCapability(cap.id, { name }), 'Capacité renommée')} />
        <span className="ml-2 font-mono text-xs text-gray-400">{cap.id}</span>
        {cap.origin === 'dashboard' && <span className="ml-2 text-xs text-blue-700">ajoutée par {cap.editedBy}</span>}
        {cap.origin === 'map' && cap.editedBy && <span className="ml-2 text-xs text-gray-400">modifiée par {cap.editedBy}</span>}
      </span>
      <span className="flex items-center gap-3 whitespace-nowrap">
        {isTested(cap) && <span className="text-xs text-gray-500 tabular-nums">{cap.tests} test{cap.tests > 1 ? 's' : ''} · {cap.levels.join('+')}</span>}
        <button
          type="button"
          onClick={() => act(functionalCoverageService.updateCapability(cap.id, { status: outOfScope ? 'active' : 'out_of_scope' }), outOfScope ? 'Capacité réintégrée' : 'Capacité marquée hors périmètre')}
          className="text-xs text-gray-500 hover:text-blue-700 underline-offset-2 hover:underline"
        >
          {outOfScope ? 'Réintégrer' : 'Hors périmètre'}
        </button>
        <button type="button" title="Supprimer cette capacité" onClick={remove} className="text-gray-400 hover:text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
      </span>
    </li>
  );
}

function FeatureCard({ feature, open, onToggle, act }) {
  const [newCap, setNewCap] = useState('');
  const [comment, setComment] = useState('');
  const [since, setSince] = useState(feature.since);
  useEffect(() => setSince(feature.since), [feature.since]);
  const s = stats([feature]);

  const addCap = async () => {
    if (newCap.trim() && (await act(functionalCoverageService.addCapability(feature.id, newCap.trim()), 'Capacité ajoutée'))) setNewCap('');
  };
  const postComment = async () => {
    if (comment.trim() && (await act(functionalCoverageService.addComment(feature.id, comment.trim()), 'Commentaire publié'))) setComment('');
  };
  const removeFeature = () => {
    if (window.confirm(`Supprimer la fonctionnalité « ${feature.name} » et ses ${plural(feature.capabilities.length, 'capacité')} ?${testsWarning(feature.tests)}\n\nRestaurable depuis l'onglet Supprimées, avec ses capacités et ses commentaires.`)) {
      act(functionalCoverageService.deleteFeature(feature.id), 'Fonctionnalité supprimée');
    }
  };
  const saveSince = () => {
    if (since.trim() && since.trim() !== feature.since) act(functionalCoverageService.updateFeature(feature.id, { since: since.trim() }), 'Version mise à jour');
  };

  return (
    <div className={`bg-white rounded-lg border ${open ? 'border-blue-400 shadow' : 'border-gray-200'}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} className="w-full flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        <span className="flex-1 min-w-[200px]">
          <span className="font-semibold text-gray-900">{feature.name}</span>
          <span className="ml-2 font-mono text-xs text-gray-400">{feature.id}</span>
          {feature.origin === 'dashboard' && <span className="ml-2 text-xs text-blue-700">ajoutée par {feature.createdBy}</span>}
        </span>
        <span className={`text-xs font-mono px-1.5 py-0.5 rounded border ${CRIT_CLS[feature.criticality] || CRIT_CLS.P3}`}>{feature.criticality || '?'}</span>
        {feature.since !== 'unknown' && <span className="text-xs font-mono px-1.5 py-0.5 rounded border border-gray-200 text-gray-600">depuis {feature.since}</span>}
        <span className={`text-xs px-2 py-0.5 rounded-full ${REVIEW_CLS[feature.reviewStatus]}`}>{REVIEW_LABELS[feature.reviewStatus]}</span>
        {feature.comments.length > 0 && <span className="flex items-center gap-1 text-xs text-gray-500"><MessageSquare className="w-3.5 h-3.5" />{feature.comments.length}</span>}
        {s.capsTested === 0 && s.caps > 0 && <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-800">aucun test</span>}
        <Bar value={s.capsTested} total={s.caps} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2 text-gray-600">Criticité
              <select value={feature.criticality || ''} onChange={(e) => act(functionalCoverageService.updateFeature(feature.id, { criticality: e.target.value }), 'Criticité mise à jour')} className="border border-gray-300 rounded px-2 py-1">
                {['P1', 'P2', 'P3'].map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-gray-600">Depuis la version
              <input
                value={since}
                onChange={(e) => setSince(e.target.value)}
                onBlur={saveSince}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                placeholder="ex. 26.1 ou unknown"
                className="w-28 border border-gray-300 rounded px-2 py-1 font-mono"
              />
            </label>
            <label className="flex items-center gap-2 text-gray-600">Revue
              <select value={feature.reviewStatus} onChange={(e) => act(functionalCoverageService.updateFeature(feature.id, { reviewStatus: e.target.value }), 'Statut de revue mis à jour')} className="border border-gray-300 rounded px-2 py-1">
                {Object.entries(REVIEW_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            {feature.editedBy && <span className="text-xs text-gray-400">Modifiée par {feature.editedBy} le {fmtDate(feature.editedAt)}</span>}
          </div>

          <div className="space-y-1 text-sm">
            <div className="flex gap-2"><span className="w-24 shrink-0 text-gray-500">Nom</span>
              <InlineText value={feature.name} onSave={(name) => act(functionalCoverageService.updateFeature(feature.id, { name }), 'Nom mis à jour')} className="text-gray-900" />
            </div>
            <div className="flex gap-2"><span className="w-24 shrink-0 text-gray-500">Description</span>
              <InlineText multiline value={feature.description} placeholder="Aucune description" onSave={(description) => act(functionalCoverageService.updateFeature(feature.id, { description }), 'Description mise à jour')} className="text-gray-700" />
            </div>
          </div>

          <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Capacités</h4>
            <ul className="space-y-1">
              {feature.capabilities.map((cap) => <CapabilityRow key={cap.id} cap={cap} act={act} />)}
            </ul>
            <div className="flex gap-2 mt-2">
              <input
                value={newCap}
                onChange={(e) => setNewCap(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCap()}
                placeholder="Ajouter une capacité manquante (un comportement testable, ex. « la fusion exige le droit d'édition »)"
                className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-1.5 text-sm"
              />
              <button type="button" onClick={addCap} disabled={!newCap.trim()} className="flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
                <Plus className="w-4 h-4" />Ajouter
              </button>
            </div>
          </div>

          {feature.notes && (
            <p className="text-sm text-gray-700 bg-blue-50 border-l-4 border-blue-300 px-3 py-2 rounded-r">
              <span className="font-medium">Notes d'analyse : </span>{feature.notes}
            </p>
          )}

          <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Discussion</h4>
            {feature.comments.length > 0 && (
              <ul className="space-y-2 mb-2">
                {feature.comments.map((c) => (
                  <li key={c.id} className="text-sm bg-gray-50 rounded px-3 py-2">
                    <div className="text-xs text-gray-500 mb-0.5"><span className="font-medium text-gray-700">{c.by}</span> · {fmtDate(c.at)}</div>
                    <div className="whitespace-pre-wrap text-gray-800">{c.text}</div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Une question, un doute sur le découpage, une capacité à préciser…" className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-1.5 text-sm" />
              <button type="button" onClick={postComment} disabled={!comment.trim()} className="self-end px-3 py-1.5 text-sm rounded bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-40">Publier</button>
            </div>
          </div>

          {feature.files.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-800">Fichiers de tests rattachés ({feature.files.length}, {feature.tests} cas)</summary>
              <ul className="mt-2 pl-5 list-disc font-mono text-xs text-gray-600 space-y-0.5 break-all">
                {feature.files.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </details>
          )}

          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button type="button" onClick={removeFeature} className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600">
              <Trash2 className="w-4 h-4" />Supprimer la fonctionnalité
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ history, featureName }) {
  if (!history.length) return <p className="text-gray-500 p-6">Aucune modification pour l'instant.</p>;
  const describe = (h) => {
    if (h.action === 'feature.update' || h.action === 'capability.update') {
      return Object.entries(h.detail?.after || {})
        .map(([k, v]) => `${FIELD_LABELS[k] || k} : ${h.detail.before?.[k] ?? '—'} → ${v}`)
        .join(' ; ');
    }
    return h.detail?.name || h.detail?.text || '';
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
          <th className="py-2 px-3">Date</th><th className="py-2 px-3">Auteur</th><th className="py-2 px-3">Action</th><th className="py-2 px-3">Cible</th><th className="py-2 px-3">Détail</th>
        </tr></thead>
        <tbody>
          {history.map((h, i) => (
            <tr key={`${h.at}-${i}`} className="border-b border-gray-100 align-top">
              <td className="py-2 px-3 whitespace-nowrap tabular-nums text-gray-600">{fmtDate(h.at)}</td>
              <td className="py-2 px-3 whitespace-nowrap">{h.by}</td>
              <td className="py-2 px-3 whitespace-nowrap">{ACTION_LABELS[h.action] || h.action}</td>
              <td className="py-2 px-3"><span className="font-mono text-xs text-gray-500">{h.target}</span><div className="text-gray-700">{featureName(h.target)}</div></td>
              <td className="py-2 px-3 text-gray-700">{describe(h)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewFeatureForm({ domains, defaultDomain, act, onCreated }) {
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState(defaultDomain || domains[0]?.[0] || '');
  const [name, setName] = useState('');
  const [criticality, setCriticality] = useState('P2');
  const [description, setDescription] = useState('');
  useEffect(() => { if (defaultDomain) setDomain(defaultDomain); }, [defaultDomain]);
  const domainName = domains.find(([code]) => code === (defaultDomain || domain))?.[1];

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1 text-sm text-blue-700 hover:underline">
        <Plus className="w-4 h-4" />Nouvelle fonctionnalité{defaultDomain && domainName ? ` dans ${domainName}` : ''}
      </button>
    );
  }
  const reset = () => { setName(''); setDescription(''); setCriticality('P2'); setOpen(false); };
  const create = async () => {
    const res = await act(functionalCoverageService.addFeature(domain, { name: name.trim(), criticality, description: description.trim() }), 'Fonctionnalité ajoutée');
    if (res) { reset(); onCreated(res.id, domain); }
  };
  return (
    <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-4 space-y-3 text-sm">
      <h3 className="font-medium text-gray-900">Nouvelle fonctionnalité</h3>
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-2 text-gray-600">Domaine
          <select value={domain} onChange={(e) => setDomain(e.target.value)} className="border border-gray-300 rounded px-2 py-1">
            {domains.map(([code, label]) => <option key={code} value={code}>{code} · {label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-gray-600">Criticité
          <select value={criticality} onChange={(e) => setCriticality(e.target.value)} className="border border-gray-300 rounded px-2 py-1">
            {['P1', 'P2', 'P3'].map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && create()}
        placeholder="Nom de la fonctionnalité (ex. « Export d'une exploration en PDF »)" className="w-full border border-gray-300 rounded px-3 py-1.5" />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Description (facultative) : ce que l'utilisateur peut faire"
        className="w-full border border-gray-300 rounded px-3 py-1.5" />
      <div className="flex gap-2">
        <button type="button" onClick={create} disabled={!name.trim() || !domain} className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">Créer</button>
        <button type="button" onClick={reset} className="px-3 py-1.5 rounded text-gray-700 hover:bg-gray-100">Annuler</button>
      </div>
      <p className="text-xs text-gray-500">Elle est créée sans capacité : dépliez-la ensuite pour décrire ses capacités.</p>
    </div>
  );
}

function RemovedTab({ removed, act }) {
  if (!removed.length) return <p className="text-gray-500 p-6">Aucun élément supprimé.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
          <th className="py-2 px-3">Supprimé le</th><th className="py-2 px-3">Par</th><th className="py-2 px-3">Élément</th><th className="py-2 px-3" />
        </tr></thead>
        <tbody>
          {removed.map((r) => (
            <tr key={r.id} className="border-b border-gray-100 align-top">
              <td className="py-2 px-3 whitespace-nowrap tabular-nums text-gray-600">{fmtDate(r.at)}</td>
              <td className="py-2 px-3 whitespace-nowrap">{r.by}</td>
              <td className="py-2 px-3">
                <span className="text-xs text-gray-500 mr-2">{r.kind === 'feature' ? 'Fonctionnalité' : 'Capacité'}</span>
                <span className="text-gray-900">{r.name}</span>
                <span className="ml-2 font-mono text-xs text-gray-400">{r.id}</span>
                {r.featureName && <div className="text-xs text-gray-500">dans {r.featureName}</div>}
              </td>
              <td className="py-2 px-3 text-right">
                <button type="button" onClick={() => act(functionalCoverageService.restore(r.id), r.kind === 'feature' ? 'Fonctionnalité restaurée' : 'Capacité restaurée')} className="inline-flex items-center gap-1 text-blue-700 hover:underline">
                  <RotateCcw className="w-4 h-4" />Restaurer
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FunctionalCoveragePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('map');
  const [domain, setDomain] = useState('');
  const [crit, setCrit] = useState('');
  const [review, setReview] = useState('');
  const [gapsOnly, setGapsOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(() => new Set());

  useEffect(() => {
    functionalCoverageService.load()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Applique une écriture : le serveur renvoie la vue à jour, on la prend telle quelle.
  const act = useCallback(async (promise, success) => {
    try {
      const res = await promise;
      setData(res.data);
      toast.success(success);
      return res;
    } catch (e) {
      toast.error(e.message);
      return false;
    }
  }, []);

  const domains = useMemo(() => Object.entries(data?.domains || {}), [data]);
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.features
      .filter((f) => (!domain || f.domain === domain) && (!crit || f.criticality === crit) && (!review || f.reviewStatus === review))
      .filter((f) => !gapsOnly || f.capabilities.some((c) => inScope(c) && !isTested(c)))
      .filter((f) => !q || [f.id, f.name, f.description, ...f.capabilities.map((c) => `${c.id} ${c.name}`)].join(' ').toLowerCase().includes(q))
      .sort((a, b) => a.domain.localeCompare(b.domain) || (a.criticality || 'P9').localeCompare(b.criticality || 'P9') || a.name.localeCompare(b.name, 'fr'));
  }, [data, domain, crit, review, gapsOnly, query]);

  const featureName = useCallback((target) => {
    const f = data?.features.find((x) => target === x.id || target.startsWith(`${x.id}-`));
    if (f) return f.name;
    // Fonctionnalité supprimée depuis : son nom n'est plus que dans la liste des suppressions.
    const r = data?.removed?.find((x) => x.kind === 'feature' && (target === x.id || target.startsWith(`${x.id}-`)));
    return r ? r.name : '';
  }, [data]);

  if (loading) return <div className="flex justify-center items-center h-screen">Chargement de la couverture fonctionnelle…</div>;
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">Impossible de charger la couverture fonctionnelle</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const total = stats(data.features);
  const removed = data.removed || [];

  // Copie hors serveur de ce qui est affiché : carte, modifications, commentaires,
  // suppressions et historique. De quoi relire le travail des PO même sans accès
  // au dashboard ; la restauration, elle, part des archives du serveur.
  const exportJson = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `couverture-fonctionnelle-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (id) => setOpen((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-3">
          <div className="flex items-center gap-3">
            <Layers className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Couverture fonctionnelle</h1>
            <button type="button" onClick={exportJson} title="Télécharger la carte et les modifications au format JSON"
              className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
              <Download className="w-4 h-4" />Exporter
            </button>
          </div>
          <p className="text-gray-600">
            Ce que fait Argonos, découpé en domaines, fonctionnalités et capacités, et ce que les tests automatisés en couvrent.
            Carte générée depuis argonos-robot ({data.source?.ref} @ {data.source?.commit}) le {fmtDate(data.generatedAt)} · {data.totalTests} cas de test rattachés.
          </p>
          <div className="flex items-start gap-3 bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded-r text-sm text-amber-900">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p><strong>Brouillon à faire valider par les PO.</strong> Les capacités ont surtout été déduites des tests existants : une capacité que personne n'a encore décrite n'apparaît pas, donc le taux est optimiste.</p>
              <p className="mt-1">Ajoutez les capacités manquantes, corrigez la criticité et la version d'apparition, puis passez la fonctionnalité en « Validée PO ». Chaque modification est enregistrée avec son auteur.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-5"><div className="text-3xl font-bold text-blue-600 tabular-nums">{pct(total.capsTested, total.caps)}%</div><div className="text-sm text-gray-600 mt-1">Capacités testées ({total.capsTested}/{total.caps})</div></div>
          <div className="bg-white rounded-lg shadow p-5"><div className="text-3xl font-bold text-red-600 tabular-nums">{pct(total.p1Tested, total.p1)}%</div><div className="text-sm text-gray-600 mt-1">Capacités P1 testées ({total.p1Tested}/{total.p1})</div></div>
          <div className="bg-white rounded-lg shadow p-5"><div className="text-3xl font-bold text-orange-600 tabular-nums">{total.untested}</div><div className="text-sm text-gray-600 mt-1">Fonctionnalités sans aucun test (sur {total.features})</div></div>
          <div className="bg-white rounded-lg shadow p-5"><div className="text-3xl font-bold text-green-600 tabular-nums">{total.validated}/{total.features}</div><div className="text-sm text-gray-600 mt-1">Fonctionnalités validées par les PO</div></div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200 px-6 py-4"><h2 className="text-lg font-semibold text-gray-900">Couverture par domaine</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <th className="py-2 px-4">Domaine</th><th className="py-2 px-4 text-right">Fonctionnalités</th><th className="py-2 px-4 text-right">Sans test</th>
                <th className="py-2 px-4">Capacités testées</th><th className="py-2 px-4 text-right">P1 testées</th><th className="py-2 px-4">Revue PO</th><th className="py-2 px-4 text-right">Tests</th>
              </tr></thead>
              <tbody>
                {domains.map(([code, name]) => {
                  const s = stats(data.features.filter((f) => f.domain === code));
                  return (
                    <tr key={code} onClick={() => { setDomain(code); setTab('map'); }} className={`border-b border-gray-100 cursor-pointer hover:bg-blue-50 ${domain === code ? 'bg-blue-50' : ''}`}>
                      <td className="py-2 px-4"><span className="font-mono text-xs text-gray-500 mr-2">{code}</span>{name}</td>
                      <td className="py-2 px-4 text-right tabular-nums">{s.features}</td>
                      <td className="py-2 px-4 text-right tabular-nums">{s.untested || '–'}</td>
                      <td className="py-2 px-4"><Bar value={s.capsTested} total={s.caps} /></td>
                      <td className="py-2 px-4 text-right tabular-nums">{s.p1Tested}/{s.p1}</td>
                      <td className="py-2 px-4"><Bar value={s.validated} total={s.features} color="bg-blue-500" /></td>
                      <td className="py-2 px-4 text-right tabular-nums">{data.domainTests?.[code] ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-6 py-3 text-xs text-gray-500">Une capacité est testée si au moins un test y est rattaché, y compris wip ou unstable ; le résultat des runs n'est pas encore pris en compte. Cliquez sur un domaine pour filtrer la carte.</p>
        </div>

        <div className="bg-white rounded-lg shadow">
          <nav className="flex space-x-8 px-6 border-b border-gray-200" role="tablist">
            {[['map', 'Carte', Layers], ['history', 'Historique des modifications', History], ['removed', 'Supprimées', Trash2]].map(([key, label, Icon]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium ${tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-700 hover:text-blue-600'}`}>
                <Icon className="w-4 h-4" />{label}{key === 'history' && data.history.length > 0 && <span className="text-xs text-gray-500">({data.history.length})</span>}
                {key === 'removed' && removed.length > 0 && <span className="text-xs text-gray-500">({removed.length})</span>}
              </button>
            ))}
          </nav>

          {tab === 'history' && <HistoryTab history={data.history} featureName={featureName} />}
          {tab === 'removed' && <RemovedTab removed={removed} act={act} />}
          {tab === 'map' && (
            <div className="p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <select value={domain} onChange={(e) => setDomain(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5">
                  <option value="">Tous les domaines</option>
                  {domains.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
                </select>
                <select value={crit} onChange={(e) => setCrit(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5">
                  <option value="">Toutes criticités</option>{['P1', 'P2', 'P3'].map((c) => <option key={c}>{c}</option>)}
                </select>
                <select value={review} onChange={(e) => setReview(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5">
                  <option value="">Tous statuts de revue</option>{Object.entries(REVIEW_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <label className="flex items-center gap-2 text-gray-700">
                  <input type="checkbox" checked={gapsOnly} onChange={(e) => setGapsOnly(e.target.checked)} />Avec des capacités non testées
                </label>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher : fusion, webhook, DE-CR…" className="w-full border border-gray-300 rounded pl-8 pr-3 py-1.5" />
                </div>
                {(domain || crit || review || gapsOnly || query) && (
                  <button type="button" onClick={() => { setDomain(''); setCrit(''); setReview(''); setGapsOnly(false); setQuery(''); }} className="text-blue-700 hover:underline">Réinitialiser</button>
                )}
              </div>
              <NewFeatureForm
                domains={domains}
                defaultDomain={domain}
                act={act}
                onCreated={(id, code) => {
                  // Montrer la nouvelle fonctionnalité dépliée : les autres filtres pourraient la masquer.
                  setDomain(code); setCrit(''); setReview(''); setGapsOnly(false); setQuery('');
                  setOpen((prev) => new Set(prev).add(id));
                }}
              />
              <p className="text-sm text-gray-500 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{filtered.length} fonctionnalités · {stats(filtered).capsTested}/{stats(filtered).caps} capacités testées</p>
              <div className="space-y-2">
                {filtered.map((f) => <FeatureCard key={f.id} feature={f} open={open.has(f.id)} onToggle={() => toggle(f.id)} act={act} />)}
                {filtered.length === 0 && <p className="text-gray-500 py-6 text-center">Aucune fonctionnalité ne correspond à ces filtres.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
